// Norwegian (Bokmål, du) copy for /preview-first-uploads, translated from copy.en.ts. Same shape (checked by
// copy.test.ts). The second pass is always "originalene" (never "full kvalitet"). The uploader's own labels are
// in English, so they're quoted as it shows them.
import type { PreviewFirstCopy } from "./copy.en";

const no: PreviewFirstCopy = {
  seo: {
    title: "Forhåndsvisninger først, på alle abonnementer | Photohawk",
    description:
      "Nytt i nettopplasteren til Photohawk, på alle abonnementer: en liten forhåndsvisning av hvert bilde lastes opp først, så galleriet ditt kan gå på nett, selge og deles i løpet av minutter mens originalene kommer etter i bakgrunnen.",
  },
  hero: {
    badge: "Nyhet",
    eyebrow: "I nettopplasteren · Alle abonnementer",
    title: "Galleriet ditt på nett på minutter,",
    titleHighlight: "ikke timer",
    subtitle:
      "Nettopplasteren sender nå først en liten forhåndsvisning av hvert bilde. Galleriet ditt kan gå på nett med disse forhåndsvisningene, så kjøperne kan finne, kjøpe og dele bildene sine mens originalene fortsetter å lastes opp i bakgrunnen.",
    ctaPrimary: "Åpne opplasteren",
    ctaSecondary: "Se hvordan det fungerer",
    uploaderLink: "Se alt som er nytt i nettopplasteren",
  },
  steps: {
    heading: "Slik fungerer det",
    subtitle: "Forhåndsvisninger først finnes i nettopplasteren, på alle abonnementer. Når du legger til bilder, viser den hvor lang tid opplastingen tar på hver måte, og du velger «Previews first, originals later».",
    stepLabel: "Steg {n}",
    items: [
      {
        id: "add",
        title: "Legg til bildene dine",
        body: "Slipp inn bilder eller en hel mappe, akkurat som før. Nettleseren lager en liten forhåndsvisning av hvert bilde, rett på datamaskinen din.",
      },
      {
        id: "previews",
        title: "Forhåndsvisningene lastes opp først",
        body: "Forhåndsvisningene er små, så de lastes raskt opp, selv med treg forbindelse på stedet.",
      },
      {
        id: "live",
        title: "Galleriet ditt går på nett",
        body: "Publiser så snart forhåndsvisningene er oppe, eller huk av for «Publish the gallery as soon as the previews are up», så skjer det av seg selv. Kjøperne kan bla, søke, kjøpe og dele med en gang.",
      },
      {
        id: "full",
        title: "Originalene kommer etter",
        body: "Originalene fortsetter å lastes opp i bakgrunnen, inn i de samme bildene. Nedlastinger og utskrifter bruker alltid originalene.",
      },
    ],
    timeline: {
      label: "Et eksempel på en opplasting, fra start til slutt",
      previews: "Forhåndsvisninger",
      previewsTime: "minutter",
      live: "Galleriet på nett",
      full: "Originaler",
      fullTime: "i bakgrunnen",
    },
  },
  buyers: {
    heading: "Hva kjøperne ser",
    subtitle: "Ingenting nytt å lære. Kjøperne får galleriet de kjenner, bare tidligere.",
    items: [
      {
        id: "protected",
        title: "Med vannmerke og beskyttet, som vanlig",
        body: "Kjøperne blar i de samme forhåndsvisningene med vannmerke som alltid, og beskyttelsen av forhåndsvisninger fungerer på samme måte hvis galleriet ditt bruker den.",
      },
      {
        id: "search",
        title: "De finner seg selv med en gang",
        body: "Søk med selfie og startnummer fungerer på forhåndsvisningene, så folk finner bildene sine så snart galleriet er på nett.",
      },
      {
        id: "delivery",
        title: "Originaler, levert automatisk",
        body: "Et kjøp eller en gratis nedlasting leveres aldri som forhåndsvisning. Hvis noen kjøper eller laster ned et bilde før originalen har kommet, flyttes originalen først i køen, og de får den så snart den er klar.",
      },
    ],
  },
  strip: {
    heading: "Følg med mens du laster opp",
    body: "Mens forhåndsvisninger og originaler lastes opp, viser en stripe øverst i opplasteren hvor galleriet ditt står: om det er på nett ennå, hvor mange originaler som gjenstår, og når et kjøpt bilde er flyttet først i køen. Når galleriet er på nett, viser den også hvor mange som har besøkt det de siste 30 minuttene, og eiere og administratorer av arbeidsområdet ser nye bestillinger siden opplastingen startet.",
    exampleLabel: "Eksempel",
    live: "På nett med forhåndsvisninger",
    orders: "3 bestillinger siden du startet",
    visitors: "42 besøkende de siste 30 min",
    left: "1\u00a0250 originaler igjen",
    jumped: "Originalene til 2 bestilte bilder er flyttet først",
  },
  faq: {
    heading: "Spørsmål",
    items: [
      {
        q: "Må jeg holde opplasteren åpen?",
        a: "Det er best, til originalene er ferdige; stripen viser hvor mange som gjenstår. Lukker du den tidlig, går ingenting tapt. Galleriet ditt blir værende på nett med forhåndsvisningene, og neste gang du åpner det galleriet i opplasteren, får du vite hvor mange bilder som fortsatt venter. Legg til den samme mappen på nytt (også senere, hjemmefra), så sendes bare originalene som mangler.",
      },
      {
        q: "Hva om noen kjøper et bilde før originalen har kommet?",
        a: "De kan likevel kjøpe det. Mens opplasteren kjører, flyttes originalen til det bildet først i køen. Ordre-e-posten venter på filen og sendes noen minutter etter at den har kommet, så kjøperen får aldri forhåndsvisningen ved en feil. Hvis opplasteren var lukket, venter bestillingen til du legger til mappen på nytt. Opplasteren viser deg hvilke bilder en kjøper venter på, og de går først.",
      },
      {
        q: "Fungerer det for gallerier med gratis nedlasting?",
        a: "Ja. Folk kan se, søke og laste ned så snart forhåndsvisningene er oppe, og en nedlasting er aldri forhåndsvisningen. Hvis noen laster ned et bilde før originalen har kommet, flyttes originalen først i køen: nedlastingen viser at originalen lastes opp, og blir en fungerende nedlasting i det øyeblikket filen kommer, og en nedlasting på e-post sendes da også. Hver nedlasting teller én gang.",
      },
      {
        q: "Fungerer søk med selfie og startnummer på forhåndsvisningene?",
        a: "Ja. Ansikter og startnumre blir funnet på forhåndsvisningene, så søket fungerer så snart de er oppe. Mens opplasteren er åpen, sender den også skarpere nærbilder hentet fra originalene dine for å hjelpe søket.",
      },
      {
        q: "Hvilke abonnementer har det?",
        a: "Alle abonnementer, også Lite. Hvis arbeidsområdet ditt har brukt forhåndsvisninger først tidligere, blir de bildene ferdige som normalt: legg til den samme mappen på nytt, så sendes originalene deres, og bestillingene og nedlastingene kommer fortsatt fram.",
      },
      {
        q: "Koster det noe ekstra?",
        a: "Nei. Det er inkludert i alle abonnementer, og hvert bilde teller én gang: forhåndsvisningen og originalen er det samme bildet. Den eneste forskjellen er at forbindelsen din sender litt mer data totalt (omtrent 5 % mer).",
      },
      {
        q: "Fungerer det med FTP og Photohawk Studio?",
        a: "Ikke for øyeblikket. Forhåndsvisninger først er en del av nettopplasteren. Bilder som sendes med FTP eller fra Photohawk Studio, lastes opp som originaler i én omgang, som før.",
      },
      {
        q: "Er det slått på som standard?",
        a: "Nei. Hver gang du legger til bilder, viser nettopplasteren hvor lang tid de tar med og uten forhåndsvisninger først, og å sende originalene med en gang er valgt. Velg «Previews first, originals later» når du vil ha galleriet på nett tidligere; nettopplasteren husker valget ditt til neste gang. Med en lavere opplastingsoppløsning («Large» eller «Web») lastes bare nedskalerte filer opp, så da gjelder ikke forhåndsvisninger først.",
      },
    ],
  },
  cta: {
    heading: "Prøv det på neste opplasting",
    body: "Forhåndsvisninger først finnes i nettopplasteren, på alle abonnementer. Legg til bildene dine, velg forhåndsvisninger først, så kan galleriet ditt være på nett på minutter.",
    button: "Åpne opplasteren",
  },
};

export default no;
